import { canUseDOM } from '@ps/web-core-utils';
import type { CasinoTopGames } from '@ps/web-martech-cms-types';
import { trim } from 'lodash';
import { takeUntil } from 'rxjs/operators';

import {
  clearState,
  getIcon,
  isFSB,
  preload,
  setLoading,
  transformParams,
  userLoggedIn,
  userLoggedOut,
  userUpdates
} from '@swf/casino';
import type { UseWidget } from '@swf/core/hooks/useWidget';
import { useWidget } from '@swf/core/hooks/useWidget';
import { areGamesVisibleByConfig, initialState } from '@swf/core/models/casino/utils';
import type {
  ExtractDispatchFromModel,
  ExtractSelectFromModel,
  RootState,
  SelectorCreator,
  Slicer,
  WidgetEffectParams,
  WidgetModelConfig
} from '@swf/core/store';
import type { CasinoGame, StreamData } from '@swf/core/types/CasinoGame';
import { currencyChange, logout } from '@swf/core/utils/actions';

export interface CasinoTopGamesWidgetState extends StreamData<CasinoGame> {
  loading: boolean;
  title?: string;
}

const reducers = {
  set: (state: CasinoTopGamesWidgetState, payload: CasinoTopGamesWidgetState) => payload,
  setLoading
};

const effects = {
  clearState,
  preload,
  userLoggedIn,
  userLoggedOut,
  userUpdates,
  async fetch(payload: undefined, params: WidgetEffectParams<CasinoTopGames>) {
    const {
      actions,
      dispatch,
      select,
      request,
      requestStream,
      rootState,
      widget: { uid, params: body, game_service: service }
    } = params;
    const widgetDispatch: ExtractDispatchFromModel<CasinoTopGamesWidgetModel> = dispatch[uid];
    const widgetSelect: ExtractSelectFromModel<CasinoTopGamesWidgetModel> = select[uid];
    const options = { service, body: transformParams(body) };
    const onLogout = logout(actions) as NonNullable<ReturnType<typeof logout>>;

    if (
      !areGamesVisibleByConfig({
        hasState: widgetSelect.get(rootState)?.games?.length > 0,
        params,
        service
      })
    ) {
      return;
    }

    if (!canUseDOM) {
      const { body: data } = await request<CasinoTopGamesWidgetState>(options);
      widgetDispatch.set({ ...data, loading: false });
    } else {
      requestStream<CasinoTopGamesWidgetState>(options, undefined)
        .pipe(takeUntil(currencyChange(actions) as typeof actions), takeUntil(onLogout))
        .subscribe((update) => widgetDispatch.set({ ...update, loading: false }));
    }
  }
};

const selectors = (slice: Slicer<CasinoTopGamesWidgetState>, createSelector: SelectorCreator) => ({
  get() {
    return slice;
  },
  getTitle() {
    return createSelector(
      slice,
      (state: RootState, config: CasinoTopGames) => config,
      ({ title }, config) => trim(config?.display_title) || title || ''
    );
  },
  getIcon,
  isFSB
});
export interface CasinoTopGamesWidgetModel
  extends WidgetModelConfig<CasinoTopGames, CasinoTopGamesWidgetState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

export const topGamesWidgetModel: CasinoTopGamesWidgetModel = {
  state: initialState,
  reducers,
  effects,
  selectors
};

export const useTopGamesWidgetModel: UseWidget<CasinoTopGames, CasinoTopGamesWidgetModel> =
  useWidget;
